/**
 * `RequestEntry` — the single Zod contract for the Flow Requests feed
 * (flow-workflow-requests U5, Key Technical Decision #2).
 *
 * The feed at `GET /api/flow/requests` merges two producers into this one
 * shape:
 *  - `ActionExecution`s (instant actions), and
 *  - `EnrollmentItem`s whose status is `submitted` (sent plays — U9/D3).
 *
 * PRE-SEND queue rows (`unassigned|draft|ready|queued|sending`) are NOT a
 * producer and must never become one again: that was the stale-merge bug
 * the R3 ruling removed (see src/api/flow-requests.ts's header).
 *
 * The schema is the source of truth; the TS type is `z.infer` only (per the
 * "Schemas Are the Source of Truth" project rule). The consumer is U6's
 * sidebar Requests tab, which reads every field below.
 */

import { z } from 'zod';
import { ResolvedOutputSnapshotSchema } from '../schemas/action-output';

/** action = instant global action; workflow = play/queue enrollment. */
export const RequestKindSchema = z.enum(['action', 'workflow']);

/**
 * Feed-facing status. Distinct from the DB `DeliveryStatus` enum — the mapper
 * normalizes `pending|dispatching|delivered|failed` (ActionExecution) and the
 * pre-send queue state (always `queued`) into this five-state union.
 */
export const RequestStatusSchema = z.enum(['pending', 'queued', 'running', 'complete', 'failed']);
export type RequestStatus = z.infer<typeof RequestStatusSchema>;

/**
 * Resolved contact display for the row. Nullable at the top level (an
 * execution can carry no contact at all → shows the definition name, no
 * contact). Individual fields are nullable too: a denormalized-email row
 * (no matched SalesforceContact) carries `{ id: null, name: null, email }`.
 */
export const RequestContactSchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
  email: z.string().nullable(),
});
export type RequestContact = z.infer<typeof RequestContactSchema>;

export const RequestEntrySchema = z.object({
  /**
   * Stable FEED id: the raw `action_executions.id` UUID for an instant
   * action, or `ei-<enrollment_items.id>` for a submitted-play row (U9/D2).
   *
   * This is NOT the URL spelling — `/flow/requests/:requestId` prefixes an
   * execution with `ex-` (enrollment rows keep `ei-`). Map between the two
   * with `routeIdFromFeedId`/`feedIdFromRouteId` below; never hand-build
   * either spelling at a call site.
   */
  id: z.string(),
  kind: RequestKindSchema,
  contact: RequestContactSchema.nullable(),
  /** Action definition name, play name, or a safe fallback. */
  actionName: z.string(),
  status: RequestStatusSchema,
  /** ISO-8601 creation timestamp; the feed sorts desc by this. */
  ts: z.string(),
  /** Caller-supplied inputs (action) or draft fields (enrollment); null if none. */
  inputs: z.record(z.string(), z.unknown()).nullable(),
  /**
   * The parsed handler result envelope (`{ ok, code, message, meta }`) for a
   * finished action, or null (queued item, still-running row, or a row whose
   * `handler_result` column failed Zod parsing — degraded, not dropped).
   */
  response: z.unknown().nullable(),
  /**
   * The output-binding snapshot resolved at completion (from
   * `handler_result.meta.appliedSnapshot`); null when there is none.
   */
  appliedSnapshot: ResolvedOutputSnapshotSchema.nullable(),
});
export type RequestEntry = z.infer<typeof RequestEntrySchema>;

/** The endpoint returns a flat array, newest first. */
export const RequestsResponseSchema = z.array(RequestEntrySchema);
export type RequestsResponse = z.infer<typeof RequestsResponseSchema>;

// ─────────────────────────────────────────────────────────────
// Feed scope (U9 / D4)
// ─────────────────────────────────────────────────────────────

/**
 * `GET /api/flow/requests?scope=` — `mine` (the acting rep's own rows, the
 * default and the historical behavior) or `all` (everyone's, same
 * `app_home_user` role gate, same feed cap). The DETAIL endpoint is
 * deliberately not scoped at all: a pasted link must always resolve.
 */
export const RequestScopeSchema = z.enum(['mine', 'all']);
export type RequestScope = z.infer<typeof RequestScopeSchema>;

// ─────────────────────────────────────────────────────────────
// Request ids — the one place three spellings are mapped (U9 / D2)
// ─────────────────────────────────────────────────────────────

/**
 * Three spellings of "which request row":
 *
 * | spelling | looks like | who uses it |
 * |---|---|---|
 * | FEED id (`RequestEntry.id`) | `<uuid>` / `ei-42` | the list payload, `openRequestDetail`, `prependOptimisticRequest`, the `recent_request` pill |
 * | ROUTE id (`/flow/requests/:requestId`, `?request=`) | `ex-<uuid>` / `ei-42` | the deep link, Copy link, the `terra_signals` `source_link` |
 * | `source_record_id` | `enrollment_item:42` | the `terra_signals` queue arm (`src/services/terra-signals/arms/terra-queue.ts:147`) |
 *
 * Execution FEED ids stay the RAW uuid on purpose (D2): every existing
 * caller already passes a raw execution id, and renaming them would ripple
 * through ContactActionMenus/useQueueUiSignals/tests for no behavioral gain.
 * The `ex-` prefix therefore exists ONLY in the URL, where a bare uuid and a
 * bare integer would otherwise be ambiguous.
 *
 * Every helper below is pure and total — a malformed input returns `null`,
 * never throws and never fabricates a half-valid id.
 */
export const REQUEST_ROUTE_BASE = '/flow/requests';
export const ENROLLMENT_REQUEST_ID_PREFIX = 'ei-';
export const EXECUTION_REQUEST_ID_PREFIX = 'ex-';

/** `'enrollment_item:' || ei.id` — the queue arm's `source_record_id` shape. */
const ENROLLMENT_SOURCE_RECORD_PREFIX = 'enrollment_item:';

/**
 * `action_executions.id` is a Postgres `uuid` column, so any canonical
 * 8-4-4-4-12 hex string is a legal id. Deliberately NOT `z.uuid()`: that
 * also enforces RFC version/variant nibbles, which would reject a legal
 * non-v4 uuid the DB would happily store.
 */
const EXECUTION_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * `enrollment_items.id` is `Int @default(autoincrement())`, so the canonical
 * spelling is a positive integer with no sign, no leading zeros and no
 * decimal point. Strict on purpose — two spellings of the same row
 * (`ei-7`, `ei-007`) would produce two different "shareable" URLs.
 */
const ENROLLMENT_ID_RE = /^[1-9][0-9]*$/;

export type ParsedRequestRouteId =
  | { kind: 'enrollment'; enrollmentItemId: number }
  | { kind: 'execution'; executionId: string };

/** ROUTE id → the row it names, or `null` for anything unparseable (→ 404 UI). */
export function parseRequestRouteId(param: string): ParsedRequestRouteId | null {
  if (param.startsWith(ENROLLMENT_REQUEST_ID_PREFIX)) {
    const raw = param.slice(ENROLLMENT_REQUEST_ID_PREFIX.length);
    if (!ENROLLMENT_ID_RE.test(raw)) return null;
    const enrollmentItemId = Number(raw);
    if (!Number.isSafeInteger(enrollmentItemId)) return null;
    return { kind: 'enrollment', enrollmentItemId };
  }
  if (param.startsWith(EXECUTION_REQUEST_ID_PREFIX)) {
    const executionId = param.slice(EXECUTION_REQUEST_ID_PREFIX.length);
    if (!EXECUTION_UUID_RE.test(executionId)) return null;
    return { kind: 'execution', executionId };
  }
  return null;
}

/** `enrollment_items.id` → the FEED id a submitted-play row carries. */
export function enrollmentFeedId(itemId: number): string {
  return `${ENROLLMENT_REQUEST_ID_PREFIX}${itemId}`;
}

/** ROUTE id (parsed) → FEED id. Execution → the raw uuid, no prefix. */
export function feedIdFromRouteId(parsed: ParsedRequestRouteId): string {
  return parsed.kind === 'execution' ? parsed.executionId : enrollmentFeedId(parsed.enrollmentItemId);
}

/** FEED id → ROUTE id (uuid gains `ex-`, `ei-<id>` passes through); `null` if neither. */
export function routeIdFromFeedId(feedId: string): string | null {
  if (EXECUTION_UUID_RE.test(feedId)) return `${EXECUTION_REQUEST_ID_PREFIX}${feedId}`;
  const parsed = parseRequestRouteId(feedId);
  return parsed?.kind === 'enrollment' ? feedId : null;
}

/** A feed row → its ROUTE id; `null` when the row's id is unrecognizable. */
export function routeIdFromFeedEntry(entry: Pick<RequestEntry, 'id'>): string | null {
  return routeIdFromFeedId(entry.id);
}

/**
 * A feed row → its shareable path (`/flow/requests/ei-42`,
 * `/flow/requests/ex-<uuid>`). `null` rather than a `/flow/requests/undefined`
 * link when the row's id is unrecognizable — callers hide the affordance.
 */
export function requestPathFromFeedEntry(entry: Pick<RequestEntry, 'id'>): string | null {
  const routeId = routeIdFromFeedEntry(entry);
  return routeId === null ? null : `${REQUEST_ROUTE_BASE}/${routeId}`;
}

/** `terra_signals.source_record_id` (`enrollment_item:<id>`) → ROUTE id. */
export function requestRouteIdFromSourceRecordId(sourceRecordId: string): string | null {
  if (!sourceRecordId.startsWith(ENROLLMENT_SOURCE_RECORD_PREFIX)) return null;
  const raw = sourceRecordId.slice(ENROLLMENT_SOURCE_RECORD_PREFIX.length);
  if (!ENROLLMENT_ID_RE.test(raw)) return null;
  return `${ENROLLMENT_REQUEST_ID_PREFIX}${raw}`;
}

// ─────────────────────────────────────────────────────────────
// Detail payload — GET /api/flow/requests/:requestId (U9 schema, U10 producer)
// ─────────────────────────────────────────────────────────────

/**
 * One `enrollment_send_attempt` row on the wire (core.prisma:1543).
 *
 * The status list MIRRORS the `EnrollmentSendAttemptStatus` Prisma enum
 * (prisma/schema/core.prisma:1333) rather than importing it: this module is
 * shared code the browser bundles, and `@prisma/client` must never be
 * imported here. A new enum member must be added in both places — the
 * detail endpoint's tests pin the list.
 *
 * `copySnapshot` is deliberately absent from the wire (deferred): it is the
 * largest column on the row and nothing renders it yet.
 */
export const RequestSendAttemptSchema = z.object({
  id: z.number().int(),
  status: z.enum(['dispatching', 'sent', 'recovered', 'already_in_flow', 'failed', 'cancelled']),
  error: z.string().nullable(),
  /** ISO-8601; null until the attempt is claimed for dispatch. */
  dispatchStartedAt: z.string().nullable(),
  executionId: z.string().nullable(),
  correlationId: z.string().nullable(),
  /** ISO-8601. */
  createdAt: z.string(),
  /** ISO-8601. */
  updatedAt: z.string(),
});
export type RequestSendAttempt = z.infer<typeof RequestSendAttemptSchema>;

/**
 * The enrollment facts behind a play row — `null` for an `ex-` (execution)
 * detail, which has no queue item at all.
 *
 * The status list MIRRORS `EnrollmentItemStatus`
 * (prisma/schema/core.prisma:1308); same no-Prisma-in-shared-code rule as
 * `RequestSendAttemptSchema` above.
 */
export const RequestEnrollmentSchema = z.object({
  itemId: z.number().int(),
  status: z.enum(['unassigned', 'draft', 'ready', 'queued', 'sending', 'submitted']),
  lastSendError: z.string().nullable(),
  playTypeId: z.number().int().nullable(),
  playInstanceRef: z.string().nullable(),
  contactId: z.string(),
  contactEmail: z.string().nullable(),
  accountId: z.string().nullable(),
});
export type RequestEnrollment = z.infer<typeof RequestEnrollmentSchema>;

/** `GET /api/flow/requests/:requestId` (U10) — one row, fully expanded. */
export const RequestDetailSchema = z.object({
  entry: RequestEntrySchema,
  /** Ascending by `createdAt`; always empty for an execution detail. */
  attempts: RequestSendAttemptSchema.array(),
  enrollment: RequestEnrollmentSchema.nullable(),
});
export type RequestDetail = z.infer<typeof RequestDetailSchema>;
